import React, { useRef } from "react"
import { Button, Col, Row, Table } from "reactstrap"
import Switch from "components/Shared/Switch"
import LocalizedFlatpickr from "components/Common/LocalizedFlatpickr"
import { daysOfWeek } from "components/Common/constants"
import { useTranslation } from "react-i18next"
import {
  filterTodayOrFutureCalendarDates,
  formatCalendarDateString,
  getStartOfToday,
  parseCalendarDateStrings,
} from "utils/calendarDateUtils"

export const buildDefaultDeliveryDays = () =>
  daysOfWeek.map(dayOfWeek => ({
    dayOfWeek,
    enabled: true,
  }))

export const buildDefaultDeliveryTimeSlots = () => [
  {
    startTime: "09:00",
    endTime: "12:00",
    labelEn: "Morning",
    labelAr: "صباح",
  },
  {
    startTime: "12:00",
    endTime: "17:00",
    labelEn: "Afternoon",
    labelAr: "بعد الظهر",
  },
  {
    startTime: "17:00",
    endTime: "21:00",
    labelEn: "Evening",
    labelAr: "مساء",
  },
]

const isDayEnabled = value =>
  value === true || value === "true" || value === 1 || value === "1"

const normalizeDeliveryDays = (deliveryDaysOfWeek = []) => {
  const byDay = new Map()
  ;(Array.isArray(deliveryDaysOfWeek) ? deliveryDaysOfWeek : []).forEach(
    day => {
      const dayOfWeek = String(day?.dayOfWeek || "").trim()
      if (!dayOfWeek) {
        return
      }
      byDay.set(dayOfWeek, day)
    }
  )

  return daysOfWeek.map(dayOfWeek => ({
    dayOfWeek,
    enabled: byDay.has(dayOfWeek)
      ? isDayEnabled(byDay.get(dayOfWeek)?.enabled)
      : true,
  }))
}

const normalizeDeliveryTimeSlots = (deliveryTimeSlots = []) => {
  const source = deliveryTimeSlots?.length
    ? deliveryTimeSlots
    : buildDefaultDeliveryTimeSlots()

  return source.map(slot => ({
    startTime: slot.startTime || "09:00",
    endTime: slot.endTime || "12:00",
    labelEn: slot.labelEn || "",
    labelAr: slot.labelAr || "",
  }))
}

/**
 * Branch schedule settings:
 * - Holidays/blackout: always available (applies to pickup / curbside / delivery).
 * - Delivery days: delivery eligibility only.
 * - Cut-off + slots: scheduled delivery UX (hidden for ASAP/QSR brands).
 */
const DeliveryScheduleSettings = ({
  orderCutOffTime,
  setOrderCutOffTime,
  orderCutOffEnabled,
  setOrderCutOffEnabled,
  deliveryDaysOfWeek,
  setDeliveryDaysOfWeek,
  holidaysCutOffEnabled,
  setHolidaysCutOffEnabled,
  holidayBlackoutDates,
  setHolidayBlackoutDates,
  deliveryTimeSlots,
  setDeliveryTimeSlots,
  /** When true (QSR / ASAP brands), hide cut-off + slot fields. */
  hideSchedulingFields = false,
  /** Delivery weekday toggles — only relevant when delivery is enabled. */
  showDeliveryDays = true,
  /** Holiday blackout — permanent for all order types. */
  showHolidays = true,
}) => {
  const { t } = useTranslation()
  const holidayPickerRef = useRef(null)

  const toggleDeliveryDay = dayOfWeek => {
    setDeliveryDaysOfWeek(prev => {
      const current = normalizeDeliveryDays(prev)
      return current.map(day =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, enabled: !day.enabled }
          : day
      )
    })
  }

  const updateDeliveryTimeSlot = (index, field, value) => {
    setDeliveryTimeSlots(prev => {
      const current = normalizeDeliveryTimeSlots(prev)
      return current.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot
      )
    })
  }

  const removeDeliveryTimeSlot = index => {
    setDeliveryTimeSlots(prev => {
      const current = normalizeDeliveryTimeSlots(prev)
      if (current.length <= 1) {
        return current
      }
      return current.filter((_, slotIndex) => slotIndex !== index)
    })
  }

  const addDeliveryTimeSlot = () => {
    setDeliveryTimeSlots(prev => {
      const current = normalizeDeliveryTimeSlots(prev)
      return [
        ...current,
        {
          startTime: "09:00",
          endTime: "12:00",
          labelEn: "",
          labelAr: "",
        },
      ]
    })
  }

  const handleHolidayDatesChange = dates => {
    setHolidayBlackoutDates(
      filterTodayOrFutureCalendarDates(
        dates.map(formatCalendarDateString).filter(Boolean)
      )
    )
  }

  const handleHolidaysCutOffToggle = () => {
    setHolidaysCutOffEnabled(prev => {
      const next = !prev
      if (!next) {
        // Turning off clears dates so edit does not resurrect a phantom selection.
        setHolidayBlackoutDates([])
      } else {
        setHolidayBlackoutDates(current =>
          filterTodayOrFutureCalendarDates(current)
        )
      }
      return next
    })
  }

  const normalizedDays = normalizeDeliveryDays(deliveryDaysOfWeek)
  const normalizedSlots = normalizeDeliveryTimeSlots(deliveryTimeSlots)
  const showCutoffAndSlots = !hideSchedulingFields && showDeliveryDays
  const selectableHolidayDates = filterTodayOrFutureCalendarDates(
    holidayBlackoutDates
  )
  // Empty array (not undefined) so Flatpickr does not fall back to "today".
  const holidayPickerDates = selectableHolidayDates.length
    ? parseCalendarDateStrings(selectableHolidayDates)
    : []

  if (!showCutoffAndSlots && !showDeliveryDays && !showHolidays) {
    return null
  }

  return (
    <div className="mt-4">
      {(showCutoffAndSlots || showDeliveryDays) && (
        <h5 className="mb-3">{t("Delivery Schedule Settings")}</h5>
      )}
      {showCutoffAndSlots ? (
        <>
          <Row className="mb-3 align-items-center">
            <Col md={8}>
              <label className="form-label d-block">
                {t("Enable special branch cut-off time")}
              </label>
              <p className="text-muted small mb-2">
                {t(
                  "When disabled, the brand Delivery Settings cut-off is used. Enable only to override cut-off for this branch."
                )}
              </p>
              <Switch
                isChecked={!!orderCutOffEnabled}
                onClickHandler={() =>
                  setOrderCutOffEnabled(prev => !prev)
                }
              />
            </Col>
          </Row>

          {orderCutOffEnabled ? (
            <Row className="mb-3">
              <Col md={6}>
                <label className="form-label">{t("Order Cut-off Time")}</label>
                <input
                  type="time"
                  className="form-control"
                  value={orderCutOffTime || "20:00"}
                  onChange={e => setOrderCutOffTime(e.target.value)}
                />
              </Col>
            </Row>
          ) : null}

          <div className="border rounded p-3 mb-4 bg-light">
            <h6 className="mb-1">{t("Delivery Time Slot Configuration")}</h6>
            <p className="text-muted small mb-3">
              {t(
                "Define the delivery time slots shown to customers when scheduling a delivery. Add each slot with its start time, end time, and display labels in English and Arabic."
              )}
            </p>

            <Table bordered responsive className="mb-3 bg-white">
              <thead>
                <tr>
                  <th>{t("Slot Start Time")}</th>
                  <th>{t("Slot End Time")}</th>
                  <th>{t("Label (English)")}</th>
                  <th>{t("Label (Arabic)")}</th>
                  <th style={{ width: 60 }}>{t("Action")}</th>
                </tr>
              </thead>
              <tbody>
                {normalizedSlots.map((slot, index) => (
                  <tr key={`delivery-slot-${index}`}>
                    <td>
                      <input
                        type="time"
                        className="form-control"
                        value={slot.startTime}
                        onChange={e =>
                          updateDeliveryTimeSlot(
                            index,
                            "startTime",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        className="form-control"
                        value={slot.endTime}
                        onChange={e =>
                          updateDeliveryTimeSlot(index, "endTime", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={slot.labelEn}
                        placeholder={t("e.g. Morning")}
                        onChange={e =>
                          updateDeliveryTimeSlot(index, "labelEn", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        dir="rtl"
                        value={slot.labelAr}
                        placeholder={t("e.g. صباح")}
                        onChange={e =>
                          updateDeliveryTimeSlot(index, "labelAr", e.target.value)
                        }
                      />
                    </td>
                    <td className="text-center align-middle">
                      {normalizedSlots.length > 1 ? (
                        <i
                          className="bx bxs-trash font-size-18 cursor-pointer text-danger"
                          title={t("Delete")}
                          onClick={() => removeDeliveryTimeSlot(index)}
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Button
              type="button"
              color="primary"
              className="d-block ms-auto"
              onClick={addDeliveryTimeSlot}
            >
              {t("Add More Slot")}
            </Button>
          </div>
        </>
      ) : null}

      {showDeliveryDays ? (
        <>
          <h6 className="mb-2">{t("Delivery Days of Week")}</h6>
          <Table
            bordered
            responsive
            className="mb-4 delivery-days-of-week-table"
          >
            <thead>
              <tr>
                <th>{t("Day")}</th>
                <th style={{ width: 120 }}>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {normalizedDays.map(day => (
                <tr key={day.dayOfWeek}>
                  <td className="align-middle text-nowrap">
                    {t(day.dayOfWeek)}
                  </td>
                  <td className="align-middle">
                    <Switch
                      isChecked={day.enabled}
                      onClickHandler={() => toggleDeliveryDay(day.dayOfWeek)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : null}

      {showHolidays ? (
        <>
          <h5 className="mb-3">{t("Holiday / Blackout Dates")}</h5>
          <p className="text-muted small mb-3">
            {t(
              "Applies to pickup, curbside, and delivery. When enabled, the branch will not accept orders on the selected dates."
            )}
          </p>
          <Row className="mb-3 align-items-center">
            <Col md={6}>
              <label className="form-label d-block">
                {t("Enable Holiday Blackout Dates")}
              </label>
              <Switch
                isChecked={!!holidaysCutOffEnabled}
                onClickHandler={handleHolidaysCutOffToggle}
              />
            </Col>
          </Row>

          {holidaysCutOffEnabled ? (
            <Row>
              <Col md={8}>
                <label className="form-label">
                  {t("Holiday / Blackout Dates")}
                </label>
                <div
                  className={`form-control position-relative d-flex flex-wrap align-items-center gap-2${
                    !selectableHolidayDates.length ? " is-invalid border-danger" : ""
                  }`}
                  style={{
                    minHeight: "42px",
                    height: "auto",
                    cursor: "pointer",
                    paddingTop: "0.4rem",
                    paddingBottom: "0.4rem",
                  }}
                  onClick={() => holidayPickerRef.current?.flatpickr?.open()}
                >
                  {[...selectableHolidayDates]
                    .filter(Boolean)
                    .sort()
                    .map(dateValue => (
                      <span
                        key={dateValue}
                        className="badge bg-primary d-inline-flex align-items-center gap-1 px-2 py-1"
                        style={{ fontSize: "0.85rem", fontWeight: 500 }}
                      >
                        {dateValue}
                        <button
                          type="button"
                          className="btn btn-link p-0 text-white lh-1"
                          style={{
                            fontSize: "1rem",
                            textDecoration: "none",
                            lineHeight: 1,
                          }}
                          aria-label={t("Remove")}
                          title={t("Remove")}
                          onClick={e => {
                            e.stopPropagation()
                            setHolidayBlackoutDates(prev =>
                              (prev || []).filter(d => d !== dateValue)
                            )
                          }}
                        >
                          <i className="bx bx-x" />
                        </button>
                      </span>
                    ))}
                  {!selectableHolidayDates.length ? (
                    <span className="text-muted">{t("Select dates")}</span>
                  ) : null}
                  <LocalizedFlatpickr
                    key={`holiday-picker-${selectableHolidayDates.join(",") || "empty"}`}
                    ref={holidayPickerRef}
                    className="position-absolute"
                    style={{
                      opacity: 0,
                      width: 1,
                      height: 1,
                      pointerEvents: "none",
                    }}
                    options={{
                      mode: "multiple",
                      dateFormat: "Y-m-d",
                      minDate: getStartOfToday(),
                      // Explicit empty — Flatpickr otherwise highlights today.
                      defaultDate: holidayPickerDates,
                      altInput: false,
                      clickOpens: true,
                      allowInput: false,
                    }}
                    value={holidayPickerDates}
                    onChange={handleHolidayDatesChange}
                  />
                </div>
                {!selectableHolidayDates.length ? (
                  <div className="invalid-feedback d-block mt-1">
                    {t("Please select at least one holiday / blackout date")}
                  </div>
                ) : (
                  <small className="text-muted d-block mt-1">
                    {t("Only today and future dates can be selected.")}
                  </small>
                )}
              </Col>
            </Row>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export default DeliveryScheduleSettings
